<template>
  <div class="crom-skeleton" :class="[`crom-skeleton--${variant}`]" aria-hidden="true">
    <!-- 1. Full Variant -->
    <div v-if="variant === 'full'" class="crom-skeleton-full-layout">
      <div class="crom-skeleton-full-body">
        <!-- Left Column: Structured Details -->
        <div class="crom-skeleton-full-details-col">
          <!-- Rating Summary Box -->
          <div class="crom-skeleton-rating-summary-box">
            <div class="crom-skeleton-rating-summary-box__rating crom-shimmer"></div>
            <div class="crom-skeleton-rating-summary-box__text">
              <div class="crom-skeleton-risk-label crom-shimmer"></div>
              <div class="crom-skeleton-rating-agency-val crom-shimmer"></div>
            </div>
          </div>

          <!-- Structured List rows -->
          <div class="crom-skeleton-details-rows-col">
            <div v-for="i in 3" :key="i" class="crom-skeleton-row-clean">
              <div class="crom-skeleton-label crom-shimmer"></div>
              <div class="crom-skeleton-value crom-shimmer"></div>
            </div>
          </div>
        </div>

        <!-- Right Column: Semicircle Gauge -->
        <div class="crom-skeleton-full-gauge-col">
          <div class="crom-skeleton-svg-container">
            <svg viewBox="0 0 600 346" width="100%">
              <!-- Background semi-circle ring -->
              <path 
                d="M115,262 A165,165 0 0 1 445,262 L372,262 A92,92 0 0 0 188,262 Z" 
                class="crom-skeleton-gauge-path" 
                stroke="#fff" 
                stroke-width="1.5" 
              />
              <!-- Bottom separating line -->
              <line x1="14" y1="284" x2="546" y2="284" stroke="#c9cfe0" opacity="0.4" />
              <!-- Bottom labels placeholder -->
              <rect x="20" y="292" width="60" height="12" rx="4" class="crom-skeleton-rect" />
              <rect x="220" y="292" width="60" height="12" rx="4" class="crom-skeleton-rect" />
            </svg>
          </div>
        </div>
      </div>

      <!-- Disclaimer -->
      <div v-if="showDisclaimer" class="crom-skeleton-disclaimer">
        <div class="crom-skeleton-disclaimer-line crom-shimmer"></div>
        <div class="crom-skeleton-disclaimer-line crom-shimmer" style="width: 75%;"></div>
      </div>
    </div>

    <!-- 2. Compact Variant -->
    <div v-else-if="variant === 'compact'" class="crom-skeleton-compact-layout">
      <div class="crom-skeleton-compact-svg" :style="{ width: typeof compactWidth === 'number' ? `${compactWidth}px` : compactWidth }">
        <svg viewBox="0 0 140 82" width="100%">
          <path 
            d="M8,70 A62,62 0 0 1 132,70 L108,70 A38,38 0 0 0 32,70 Z" 
            class="crom-skeleton-gauge-path" 
          />
        </svg>
      </div>

      <div class="crom-skeleton-compact-info">
        <div class="crom-skeleton-compact-info__rating crom-shimmer"></div>
        <div class="crom-skeleton-compact-info__cra crom-shimmer"></div>
        <div class="crom-skeleton-compact-info__extra crom-shimmer"></div>
      </div>
    </div>

    <!-- 3. Mobile Variant -->
    <div v-else-if="variant === 'mobile'" class="crom-skeleton-mobile-layout">
      <div class="crom-skeleton-mobile-section-left">
        <div class="crom-skeleton-mobile-label crom-shimmer"></div>
        <div class="crom-skeleton-compact-svg" style="width: 86px;">
          <svg viewBox="0 0 140 82" width="100%">
            <path 
              d="M8,70 A62,62 0 0 1 132,70 L108,70 A38,38 0 0 0 32,70 Z" 
              class="crom-skeleton-gauge-path" 
            />
          </svg>
        </div>
      </div>

      <div class="crom-skeleton-mobile-rating-val crom-shimmer"></div>

      <div class="crom-skeleton-mobile-info">
        <div class="crom-skeleton-mobile-info-line crom-shimmer"></div>
        <div class="crom-skeleton-mobile-info-line crom-shimmer" style="width: 60px; margin-left: auto;"></div>
      </div>
    </div>

    <!-- 4. Card Variant -->
    <div v-else-if="variant === 'card'" class="crom-skeleton-card-layout">
      <div class="crom-skeleton-card-info">
        <div class="crom-skeleton-card-info__rating crom-shimmer"></div>
        <div class="crom-skeleton-card-info__sub crom-shimmer"></div>
      </div>
      <div class="crom-skeleton-card-svg" style="width: 66px;">
        <svg viewBox="0 0 140 82" width="100%">
          <path 
            d="M8,70 A62,62 0 0 1 132,70 L108,70 A38,38 0 0 0 32,70 Z" 
            class="crom-skeleton-gauge-path" 
          />
        </svg>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
withDefaults(defineProps<{
  variant?: 'full' | 'compact' | 'mobile' | 'card';
  showDisclaimer?: boolean;
  compactWidth?: number | string;
}>(), {
  variant: 'full',
  showDisclaimer: true,
  compactWidth: 132
});

defineOptions({ name: 'CreditRiskMeterSkeleton' });
</script>

<style scoped>
.crom-skeleton {
  font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  --crom-line: #dfe5f2;
  --crom-shimmer-bg: #eef2f7;
  --crom-shimmer-highlight: #e3e8f0;
  width: 100%;
  box-sizing: border-box;
}

/* Shimmer Animation */
.crom-shimmer {
  background: linear-gradient(90deg, #eef2f7 25%, #e3e8f0 50%, #eef2f7 75%);
  background-size: 200% 100%;
  animation: crom-skeleton-shimmer 1.5s infinite linear;
  border-radius: 4px;
}

.crom-skeleton-rect {
  fill: var(--crom-shimmer-bg);
  animation: crom-svg-shimmer 1.5s infinite ease-in-out;
}

.crom-skeleton-gauge-path {
  fill: var(--crom-shimmer-bg);
  animation: crom-svg-shimmer 1.5s infinite ease-in-out;
}

@keyframes crom-skeleton-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

@keyframes crom-svg-shimmer {
  0% { fill: var(--crom-shimmer-bg); }
  50% { fill: var(--crom-shimmer-highlight); }
  100% { fill: var(--crom-shimmer-bg); }
}

/* ----------------- Full Layout ----------------- */
.crom-skeleton-full-layout {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.crom-skeleton-full-body {
  display: flex;
  gap: 24px;
  width: 100%;
  align-items: center;
}

.crom-skeleton-full-details-col {
  flex: 1 1 45%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
}

.crom-skeleton-full-gauge-col {
  flex: 1 1 55%;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
}

.crom-skeleton-svg-container {
  width: 100%;
  max-width: 560px;
  margin: 0 auto;
}

/* Rating Summary Box */
.crom-skeleton-rating-summary-box {
  display: flex;
  align-items: center;
  gap: 12px;
  background-color: #f2eef9;
  border-radius: 12px;
  padding: 10px 14px;
  width: 100%;
}

.crom-skeleton-rating-summary-box__rating {
  width: 80px;
  height: 32px;
  border-right: 1.5px solid var(--crom-line);
  padding-right: 12px;
}

.crom-skeleton-rating-summary-box__text {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex-grow: 1;
}

.crom-skeleton-risk-label {
  width: 120px;
  height: 10px;
}

.crom-skeleton-rating-agency-val {
  width: 80px;
  height: 14px;
}

/* Structured List Rows */
.crom-skeleton-details-rows-col {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.crom-skeleton-row-clean {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px dashed var(--crom-line);
}

.crom-skeleton-row-clean:last-child {
  border-bottom: none;
}

.crom-skeleton-label {
  width: 90px;
  height: 12px;
}

.crom-skeleton-value {
  width: 70px;
  height: 12px;
}

/* Disclaimer */
.crom-skeleton-disclaimer {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 12px;
  border-top: 1px dashed var(--crom-line);
  padding-top: 12px;
}

.crom-skeleton-disclaimer-line {
  width: 100%;
  height: 10px;
}

/* ----------------- Compact Layout ----------------- */
.crom-skeleton-compact-layout {
  display: flex;
  gap: 12px;
  align-items: flex-end;
  width: auto;
}

.crom-skeleton-compact-svg {
  display: flex;
  align-items: flex-end;
  flex-shrink: 0;
}

.crom-skeleton-compact-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 110px;
}

.crom-skeleton-compact-info__rating {
  width: 50px;
  height: 18px;
}

.crom-skeleton-compact-info__cra {
  width: 75px;
  height: 12px;
}

.crom-skeleton-compact-info__extra {
  width: 90px;
  height: 12px;
}

/* ----------------- Mobile Layout ----------------- */
.crom-skeleton-mobile-layout {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 8px 0;
}

.crom-skeleton-mobile-section-left {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.crom-skeleton-mobile-label {
  width: 50px;
  height: 12px;
}

.crom-skeleton-mobile-rating-val {
  width: 60px;
  height: 28px;
}

.crom-skeleton-mobile-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 90px;
}

.crom-skeleton-mobile-info-line {
  width: 80px;
  height: 12px;
}

/* ----------------- Card Layout ----------------- */
.crom-skeleton-card-layout {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  background: var(--crom-bg, #f4f7fc);
  border: 1px solid var(--crom-line);
  border-radius: 8px;
  padding: 8px 12px;
  box-shadow: none;
}

.crom-skeleton-card-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex-grow: 1;
}

.crom-skeleton-card-info__rating {
  width: 65px;
  height: 17px;
}

.crom-skeleton-card-info__sub {
  width: 130px;
  height: 12px;
}

.crom-skeleton-card-svg {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-shrink: 0;
}

/* Mobile responsive adjustments */
@media (max-width: 768px) {
  .crom-skeleton-full-body {
    flex-direction: column;
    gap: 16px;
  }
  
  .crom-skeleton-full-details-col {
    width: 100%;
    order: 2;
  }
  
  .crom-skeleton-full-gauge-col {
    width: 100%;
    order: 1;
  }
}
</style>